import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ImpersonationBanner from './ImpersonationBanner';

const Layout = ({ children }) => {
  const { user, logout, isAdmin, canAccess, roleLabel, isImpersonating } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar on Escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') setSidebarOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const allNavItems = [
    { path: '/', label: 'Home', section: 'dashboard', badge: 'Overview', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/> },
    { path: '/dashboard', label: 'Dashboard', section: 'analytics', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/> },
    { type: 'label', label: 'Analytics' },
    { path: '/tools/subid-finder', label: 'Sub ID Finder', section: 'subid', icon: <><circle cx="11" cy="11" r="8" strokeWidth="2"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35"/></> },
    { path: '/tools/decline-analyzer', label: 'Decline Analyzer', section: 'decline', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/> },
    { path: '/tools/quality-report', label: 'Quality Report', section: 'quality', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/> },
    { path: '/tools/keitaro-conversions', label: 'Statistics', section: 'statistics', badge: 'Live', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/> },
    { path: '/tools/keitaro-manager', label: 'Keitaro Manager', section: 'keitaro-manager', icon: <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><circle cx="12" cy="12" r="3" strokeWidth="2"/></> },
    { type: 'label', label: 'Management' },
    { path: '/buyer-teams', label: 'Buyer & Teams', section: 'teams', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/> },
    { path: '/billing', label: 'Billing', section: 'billing', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/> },
    { path: '/traffic-shield', label: 'Traffic Shield', section: 'traffic-shield', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/> },
    { path: '/kpi', label: 'KPI', section: 'kpi', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/> },
    { path: '/security', label: 'Security', section: 'security', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/> },
  ];

  const adminItems = [
    { path: '/users', label: 'Users', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/> },
    { path: '/logs', label: 'Logs', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/> },
  ];

  const navItems = allNavItems.filter(item =>
    item.type === 'label' || canAccess(item.section)
  );

  const pageInfo = {
    '/': { title: 'Home', iconBg: 'rgba(79,142,255,.12)', iconBorder: 'rgba(79,142,255,.2)', iconColor: 'var(--blue)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/> },
    '/dashboard': { title: 'Dashboard', iconBg: 'rgba(79,142,255,.12)', iconBorder: 'rgba(79,142,255,.2)', iconColor: 'var(--blue)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/> },
    '/tools/subid-finder': { title: 'Sub ID Finder', iconBg: 'rgba(79,142,255,.12)', iconBorder: 'rgba(79,142,255,.2)', iconColor: 'var(--blue)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/> },
    '/tools/decline-analyzer': { title: 'Decline Analyzer', iconBg: 'rgba(255,159,67,.12)', iconBorder: 'rgba(255,159,67,.2)', iconColor: 'var(--orange)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"/> },
    '/tools/quality-report': { title: 'Quality Report', iconBg: 'rgba(0,229,160,.10)', iconBorder: 'rgba(0,229,160,.2)', iconColor: 'var(--green)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/> },
    '/tools/keitaro-conversions': { title: 'Statistics', iconBg: 'rgba(124,111,255,.10)', iconBorder: 'rgba(124,111,255,.2)', iconColor: 'var(--indigo)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/> },
    '/tools/keitaro-manager': { title: 'Keitaro Manager', iconBg: 'rgba(255,211,42,.10)', iconBorder: 'rgba(255,211,42,.2)', iconColor: 'var(--yellow)', icon: <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><circle cx="12" cy="12" r="3" strokeWidth="2"/></> },
    '/buyer-teams': { title: 'Buyer & Teams', iconBg: 'rgba(79,142,255,.12)', iconBorder: 'rgba(79,142,255,.2)', iconColor: 'var(--blue)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/> },
    '/billing': { title: 'Billing', iconBg: 'rgba(255,107,157,.10)', iconBorder: 'rgba(255,107,157,.2)', iconColor: 'var(--pink)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/> },
    '/traffic-shield': { title: 'Traffic Shield', iconBg: 'rgba(0,229,160,.10)', iconBorder: 'rgba(0,229,160,.22)', iconColor: 'var(--green)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/> },
    '/kpi': { title: 'KPI Dashboard', iconBg: 'rgba(0,229,160,.10)', iconBorder: 'rgba(0,229,160,.22)', iconColor: 'var(--green)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/> },
    '/security': { title: 'Security Settings', iconBg: 'rgba(255,159,67,.10)', iconBorder: 'rgba(255,159,67,.2)', iconColor: 'var(--orange)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/> },
    '/users': { title: 'Users', iconBg: 'rgba(124,111,255,.10)', iconBorder: 'rgba(124,111,255,.2)', iconColor: 'var(--indigo)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/> },
    '/logs': { title: 'Activity Logs', iconBg: 'rgba(124,111,255,.10)', iconBorder: 'rgba(124,111,255,.2)', iconColor: 'var(--indigo)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/> },
  };

  const currentPage = pageInfo[location.pathname] || { title: 'TH Tools', iconBg: 'rgba(79,142,255,.12)', iconBorder: 'rgba(79,142,255,.2)', iconColor: 'var(--blue)', icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/> };
  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : 'U';

  return (
    <>
      {/* Impersonation banner -- above everything */}
      {isImpersonating && <ImpersonationBanner />}

      <div className="mesh-bg" />
      <div className="grid-bg" />

      {/* Overlay backdrop */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 99, backdropFilter: 'blur(4px)',
            animation: 'fadeIn .25s cubic-bezier(0.4,0,0.2,1) both'
          }}
        />
      )}

      {/* Sidebar drawer */}
      <aside
        className="sidebar"
        style={{
          position: 'fixed', left: 0, top: 0, bottom: 0,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform .35s cubic-bezier(.22,1,.36,1)',
          zIndex: 100,
          animation: 'none'
        }}
      >
        <div className="sidebar-logo">
          <div className="logo-mark">T</div>
          <div style={{ flex: 1 }}>
            <div className="logo-text">TH Tools</div>
            <div className="logo-version">v2.4.1</div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            style={{
              background: 'none', border: 'none', color: '#6b7a9f',
              cursor: 'pointer', fontSize: '18px', padding: '4px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            title="Close sidebar"
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <nav className="nav-wrap">
          {navItems.map((item, idx) => {
            if (item.type === 'label') {
              return <div key={`label-${idx}`} className="nav-label">{item.label}</div>;
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item${location.pathname === item.path ? ' active' : ''}`}
              >
                <svg className="ni" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="15" height="15">
                  {item.icon}
                </svg>
                {item.label}
                {item.badge && <span className="nav-badge">{item.badge}</span>}
              </Link>
            );
          })}
          {isAdmin && adminItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item${location.pathname === item.path ? ' active' : ''}`}
            >
              <svg className="ni" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="15" height="15">
                {item.icon}
              </svg>
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="user-card" onClick={handleLogout} title="Click to logout">
            <div className="user-ava">{userInitial}</div>
            <div>
              <div className="user-name">{user?.name || 'User'}</div>
              <div className="user-role">{roleLabel || user?.role || 'user'}</div>
            </div>
            <div className="user-dot" />
          </div>
        </div>
      </aside>

      <main className="main" style={{ width: '100%', paddingTop: isImpersonating ? '48px' : '0' }}>
        <div className="topbar">
          <div className="topbar-left">
            {/* Hamburger menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                background: 'none', border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '8px', color: '#eef2ff', cursor: 'pointer',
                width: '36px', height: '36px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                transition: 'background .2s cubic-bezier(0.4,0,0.2,1)'
              }}
              title="Open menu"
            >
              <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
            <div className="page-icon" style={{ background: currentPage.iconBg, border: `1px solid ${currentPage.iconBorder}`, color: currentPage.iconColor }}>
              <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {currentPage.icon}
              </svg>
            </div>
            <h1 className="page-title">{currentPage.title}</h1>
          </div>
          <div className="topbar-right" />
        </div>
        <div className="content">
          <div className="page-enter" key={location.pathname}>
            {children}
          </div>
        </div>
      </main>

      {/* Keyframes (fadeIn) defined in index.css */}
    </>
  );
};

export default Layout;
